// frontend/src/views/Members.jsx

import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

// Lógica de sesión
import { getStoredSession, setStoredSession } from '../hooks/useHashSession'
import { useMembers } from '../hooks/useMembers'

import GroupHeader from '../components/GroupHeader'
import { useGroupInfo } from '../hooks/useGroupInfo'

// Servicios (miembros)
import {
  createMember,
  getMemberById,
  deleteMember,
  updateMember
} from '../services/memberService';

// Servicios (personas)
import {
  getPersonById
} from '../services/personService';

// Otros
import { updateGroupState } from '../services/groupService'

// Utilidades
import { formatDateForSQL } from '../utils/dateUtils'
import { normalizeMemberForBackend } from '../utils/normalizeMember'

// Componentes visuales
import MembersTable from '../components/MembersTable'
import MemberEditorModal from '../components/MemberEditorModal'
import MembersFiltersSidebar from '../components/MembersFiltersSidebar'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

// Translation function
import { useTranslation } from '../i18n'

export default function Members() {
  const [session, setSession] = useState(null)
  const [origin, setOrigin] = useState(null)
  const [members, setMembers] = useState([])
  const [loading, setLoading] = useState(true)
  const [isEditing, setIsEditing] = useState(false);
  const [memberToEdit, setMemberToEdit] = useState(null)
  const [memberToCreate, setMemberToCreate] = useState(null)
  const [groupInfo, setGroupInfo] = useState(null)
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [createError, setCreateError] = useState('')
  const [errorName, setErrorName] = useState('');
  const [editError, setEditError] = useState('');
  const [editErrorName, setEditErrorName] = useState('');
  const [editErrorCode, setEditErrorCode] = useState('');

  // Filtros + paginación
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    nationality: '',
    national_id: '',
    passport: '',
    first_name: '',
    last_name: ''
  });
  // Filtros en uso (solo cambian al presionar "Aplicar")
  const [appliedFilters, setAppliedFilters] = useState({
    nationality: '',
    national_id: '',
    passport: '',
    first_name: '',
    last_name: ''
  });

  // page/limit inicial desde entorno (reset a 1 al aplicar filtros)
  const envLimit = parseInt(
    process.env.REACT_APP_MEMBERS_PAGE_SIZE || '', 10
  );
  const initialLimit = Number.isFinite(envLimit) && envLimit > 0
    ? Math.min(envLimit, 100)
    : 10;

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(initialLimit);
  const [total, setTotal] = useState(0);

  const effectiveGroupInfo = useGroupInfo(groupInfo, session)

  // Derivados de estado
  const isClosed = effectiveGroupInfo?.state === 'closed'

  const isLeaderActingAsGuest = session?.role === 'guest'
    && !!session?.parentHash

  const isGuestBlocked = session?.role === 'guest'
    && !session?.parentHash
    && effectiveGroupInfo?.invitation_status === 'blocked'

  const isReadOnly = (isClosed && session?.role === 'guest') ||
    (session?.role === 'guest' && effectiveGroupInfo?.invitation_status === 'blocked')

  // Detecta si el miembro está validado (compat con varias fuentes)
  const isMemberValidated = (m = {}) => {
    const vs = String(m.validation_status || m.person_validation_status || '').toLowerCase();
    const src = String(m.validation_source || m.person_validation_source || '');
    return (vs === 'valid') || (m.validation_status === 'VERIFIED') || src.startsWith('PADRON_');
  };

  const navigate = useNavigate()
  const location = useLocation()
  const { _ } = useTranslation()
  const createdByHash = location.state?.createdByHash || null
  const navigationGroupState = location.state?.groupState || null

  // Adaptador de miembros del backend al frontend
  const adaptMemberFromBackend = (data) => ({
    id: data.id,
    personId: data.person_id ?? data.personId,
    nationalId: data.national_id ?? data.nationalId,
    passport: data.passport,
    firstName: data.first_name ?? data.firstName,
    middleName: data.middle_name ?? data.middleName,
    lastName: data.last_name ?? data.lastName,
    secondLastName: data.second_last_name ?? data.secondLastName,
    gender: data.gender,
    birthdate: data.birthdate,
    nationality: data.nationality,
    contact: data.contact,
    fingerprint: data.fingerprint,
    createdByHash: data.created_by_hash ?? data.createdByHash,
    createdAt: data.created_at ?? data.createdAt,
    updatedAt: data.updated_at ?? data.updatedAt,
    deletedAt: data.deleted_at ?? data.deletedAt,
    canEdit: true,
    validation_source: data.validation_source ?? data.validationSource ?? null,
    validation_status: data.validation_status ?? data.validationStatus ?? null,
  })

  // Banners
  const closedBanner = isClosed && session.role === 'leader' ? (
    <div className="banner-closed">
      {_('This group is closed.')}
    </div>
  ) : null

  const readOnlyBanner = isReadOnly ? (
    <div className="banner-readonly">
      <span>{_('This group is in read-only mode.')}</span>
      {session?.role === 'guest' && !session?.parentHash && (
        <span> {_('Contact the group leader to make changes.')}</span>
      )}
    </div>
  ) : null

  // 1) Inicializa la sesión considerando hash (leader/guest) o JWT + active_group_hash
  useEffect(() => {
    const stored = getStoredSession() // acceso por hash (líder o invitado)
    const jwt = sessionStorage.getItem('jwt_token')

    if (stored?.hash && stored?.role) {
      // Caso normal por hash (líder o invitado)
      setSession(stored)
      const origin = sessionStorage.getItem('group_origin')
      if (origin) setOrigin(origin)
    } else if (jwt) {
      // Caso autenticado: debe venir con hash configurado por AuthenticatedDashboard
      const hashFromOrigin = sessionStorage.getItem('active_group_hash')
      if (hashFromOrigin) {
        setSession({ hash: hashFromOrigin, role: 'leader' }) // simulación explícita de líder
        setOrigin('dashboard')
      } else {
        navigate('/')
      }
    } else {
      navigate('/')
    }
  }, [navigate])

  useEffect(() => {
    if (!session) return

    setGroupInfo(prev => ({
      ...prev,
      group_name: prev?.group_name || session?.groupName || null,
      owner_name: prev?.owner_name || session?.ownerName || null,
      state: prev?.state || navigationGroupState || null
    }))
  }, [session, navigationGroupState])

  // 3) Carga de miembros (filtros + paginación, ambos modos)
  useMembers({
    session,
    origin,
    page,
    limit,
    appliedFilters,
    createdByHash,
    setMembers,
    setTotal,
    setPage,
    setLimit,
    setLoading
  });

  // Handlers
  const handleAskDelete = (member) => setDeleteTarget(member)

  // Maneja el cierre del grupo
  const handleClose = async (group) => {
    // `session.hash` es el hash de acceso que el líder tiene para el grupo
    // seleccionado. No se necesita el JWT para esta operación.
    try {
      const updatedGroup = await updateGroupState('closed', session.hash);
      setGroupInfo(prev => ({
        ...prev,
        state: updatedGroup?.state || 'closed'
      }));
    } catch (error) {
      // Maneja el error (ej: mostrar una notificación)
      console.error("Failed to block group:", error);
    }
  };

  // Maneja la reapertura del grupo
  const handleReopen = async (group) => {
    try {
      const updatedGroup = await updateGroupState('active', session.hash);
      setGroupInfo(prev => ({
        ...prev,
        state: updatedGroup?.state || 'active'
      }));
    } catch (error) {
      console.error("Failed to unlock group:", error);
    }
  };

  const handleCancel = () => {
    setMemberToEdit(null);
    setEditError('');
    setEditErrorName('');
    setEditErrorCode('');
  };

  // Maneja la creación de un nuevo miembro 
  // enviando los datos al backend y actualizando el estado.
  // Maneja la creación de un nuevo miembro enviando los datos al backend y actualizando el estado.
  const handleCreate = async () => {
    if (!session?.hash) {
      setCreateError(_('Session error. Please reload the page.'));
      return;
    }
    if (!memberToCreate) return;

    const payload = normalizeMemberForBackend(memberToCreate);

    try {
      const created = await createMember(payload, session.hash);
      const jwt = sessionStorage.getItem('jwt_token') || undefined;

      // Intentamos obtener IDs de la respuesta del backend
      const personIdFromCreated =
        created?.person_id ?? created?.person?.id ?? null;
      const memberIdFromCreated =
        created?.id ?? created?.member_id ?? created?.member?.id ?? null;

      let adapted = adaptMemberFromBackend(created);

      // Si el adaptador no pudo deducir person_id (pasa si 'created' es de persons o es parcial),
      // lo completamos con el que detectamos arriba.
      if (personIdFromCreated && !adapted.person_id && !adapted.personId) {
        adapted = { ...adapted, person_id: personIdFromCreated, personId: personIdFromCreated };
      }
      // Conserva también el id del miembro cuando esté disponible
      if (memberIdFromCreated && !adapted.member_id && !adapted.id) {
        adapted = { ...adapted, member_id: memberIdFromCreated, id: memberIdFromCreated };
      }

      // Si aún faltan campos de persona para la UI (p. ej. nombres),
      // y tenemos personId, hidratamos con GET /api/persons/id/:id
      const needsHydration =
        !adapted.firstName && !adapted.lastName && !!personIdFromCreated;

      if (needsHydration) {
        const person = await getPersonById(personIdFromCreated, session.hash, jwt);
        const hydrated = adaptMemberFromBackend(person);
        adapted = {
          ...hydrated,
          // preserva los IDs de member/person
          person_id: personIdFromCreated,
          personId: personIdFromCreated,
          member_id: memberIdFromCreated ?? adapted.member_id,
          id: memberIdFromCreated ?? adapted.id,
        };
      }

      // Reemplaza el temporal por el definitivo y limpia estado/errores
      setMembers(prev => [
        ...prev.filter(m => m.tempId !== memberToCreate.tempId),
        adapted,
      ]);

      setMemberToCreate(null);
      setCreateError('');
      setErrorName('');
    } catch (error) {
      console.error('Error creando miembro:', error);
      setCreateError(error.message || _('Failed to create member'));

      // Si el backend devuelve conflicto con nombre sugerido
      if (error.status === 409 && error.personName) {
        setErrorName(error.personName);
      } else {
        setErrorName('');
      }
    }
  };

  // Maneja la eliminación de un miembro seleccionado.
  const handleDelete = async () => {
    if (!deleteTarget) return
    try {
      await deleteMember(deleteTarget.id, session.hash)
      setMembers(prev => prev.filter(m => m.id !== deleteTarget.id))
      setDeleteTarget(null)
    } catch (error) {
      alert(error.message || 'Failed to delete member')
    }
  }

  // Maneja los cambios en los campos del formulario de edición
  // y actualiza el estado.
  const handleChange = (e) => {
    const { name, value } = e.target
    setMemberToEdit(prev => ({ ...prev, [name]: value }))
  }

  // Maneja la carga de los datos completos de un miembro al iniciar la edición.
  const handleEdit = async (row) => {
    // IDs que podrían venir con distintos nombres
    const memberId =
      row?.member_id ?? row?.memberId ?? (Number.isFinite(row?.id)
        ? row.id : null);

    // Si es un registro temporal (recién añadido en memoria) sin persistir aún
    if (!memberId) {
      // Abre editor local con los datos que ya tenemos (sin llamar a la API)
      setMemberToCreate(prev => ({ ...prev, ...row, isTemp: true }));
      setIsEditing(true);
      return;
    }

    // 3) Guard de sesión
    if (!session || !session.hash) {
      setEditError(_('Session error. Please reload the page.'));
      return;
    }

    setEditError('');
    setEditErrorName('');
    setEditErrorCode('');

    const jwt = sessionStorage.getItem('jwt_token') || undefined;

    try {
      // 4) Cargar el miembro por ID (ruta corregida /api/members/id/:id)
      const member = await getMemberById(memberId, session.hash, jwt);

      // 5) Adaptar datos a la UI. Tu adaptador debería aceptar payloads de "member".
      //    Si tu adaptador está orientado a "persona", asegúrate de que member incluya esos campos.
      let adapted = adaptMemberFromBackend(member);

      // Compat: preservar flags person_* si vinieron desde el GET
      adapted = {
        ...adapted,
        validation_status: adapted.validation_status || member.person_validation_status || adapted.validationStatus || null,
        validation_source: adapted.validation_source || member.person_validation_source || adapted.validationSource || null,
      };

      // Garantiza que el estado tenga ambos IDs claramente
      if (!adapted.person_id && !adapted.personId) {
        const resolvedPersonId = member?.person_id ?? personId ?? adapted.personId ?? null;
        adapted = { ...adapted, person_id: resolvedPersonId, personId: resolvedPersonId };
      }

      // Asegura también el member_id/id
      if (!adapted.member_id && !adapted.id) {
        const resolvedMemberId = member?.member_id ?? member?.id ?? null;
        adapted = { ...adapted, member_id: resolvedMemberId, id: resolvedMemberId };
      }

      // 6) Garantiza que el estado tenga los IDs esperados por la UI:
      //    - member_id = groups_members.id
      //    - person_id  (si el member lo trae)
      adapted = {
        ...adapted,
        member_id: member.id ?? adapted.member_id ?? null,
        person_id: member.person_id ?? adapted.person_id ?? adapted.personId ?? null,
      };

      setMemberToEdit(adapted);
      setIsEditing(true);

    } catch (error) {
      console.error('Error al cargar los datos del miembro:', error);
      setEditError(error.message || _('Failed to load member details.'));
    }
  };

  //  Maneja el guardado de los cambios de un miembro editado usando memberId.
  const handleSave = async () => {
    const memberId = memberToEdit?.member_id ?? memberToEdit?.id ?? null;
    const personId = memberToEdit?.person_id ?? memberToEdit?.personId ?? null;

    if (!memberId) {
      setEditError(_('Missing member id to save.'));
      return;
    }

    const normalizedPerson = normalizeMemberForBackend(memberToEdit);

    // Si está validado, solo permitimos editar contact/passport desde la UI
    const payload = isMemberValidated(memberToEdit)
      ? {
        ...(Object.prototype.hasOwnProperty.call(normalizedPerson, 'contact') ? { contact: normalizedPerson.contact } : {}),
        ...(Object.prototype.hasOwnProperty.call(normalizedPerson, 'passport') ? { passport: normalizedPerson.passport } : {}),
      }
      : normalizedPerson;

    try {
      const jwt = sessionStorage.getItem('jwt_token') || undefined;

      const updatedPerson = await updateMember(
        memberId,
        payload,
        session.hash,
        jwt
      );

      let adapted = adaptMemberFromBackend(updatedPerson);

      // Asegura que el adaptado conserve los IDs que tu UI espera
      if (!adapted.person_id && !adapted.personId) {
        adapted = { ...adapted, person_id: personId, personId };
      }
      if (memberId && !adapted.member_id && !adapted.id) {
        adapted = { ...adapted, member_id: memberId, id: memberId };
      }

      // Upsert en memoria por personId
      setMembers(prev =>
        prev.map(m => {
          const mPid = m.person_id ?? m.personId;
          const aPid = adapted.person_id ?? adapted.personId;
          return mPid === aPid
            ? { ...m, ...adapted }
            : m;
        })
      );

      setMemberToEdit(null);
      setIsEditing(false);
    } catch (error) {
      console.error('Error guardando edición:', error);
      setEditError(error.message || _('Failed to save member'));
      setEditErrorName(error.personName || '');
      setEditErrorCode(error.code || (error.status === 409 ? 'E_DUP_GENERIC' : ''));
    }
  };

  // Handlers de filtros y paginación
  const toggleFilters = () => setShowFilters(v => !v);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleApplyFilters = () => {
    // Copia filtros de edición a filtros en uso y resetea a 1
    setAppliedFilters({ ...filters });
    setPage(1);
    setShowFilters(false);
  };

  const handleClearFilters = () => {
    const empty = {
      nationality: '',
      national_id: '',
      passport: '',
      first_name: '',
      last_name: ''
    };
    setFilters(empty);
    setAppliedFilters(empty);
    setPage(1);
  };

  const canPrev = page > 1;
  const totalPages = Math.max(Math.ceil(total / Math.max(limit, 1)), 1);
  const canNext = page < totalPages;

  if (!session) {
    return <div className="session-error">⚠️ {_('No active session')}</div>
  }

  // Etiquetas visibles para resumen de filtros
  const FILTER_LABELS = {
    nationality: _('Nationality'),
    national_id: _('National ID'),
    passport: _('Passport'),
    first_name: _('First Name'),
    last_name: _('Last Name')
  };

  // Filtros activos (solo si tienen valor no vacío)
  const activeFilters = Object.entries(appliedFilters || {})
    .filter(([, v]) => (v || '').trim().length > 0);

  const hasActiveFilters = activeFilters.length > 0;

  return (
    <div className="container">
      {closedBanner}
      {readOnlyBanner}

      <>
        {session.parentHash && (
          <div>
            <button
              className="icon-button text-sm text-blue-600 hover:underline"
              onClick={() => {
                const stored = getStoredSession()

                if (stored?.parentHash) {
                  setStoredSession(
                    stored.parentHash,
                    'leader',
                    null,
                    stored.groupName,
                    stored.ownerName
                  )
                }
                navigate('/invitations', {
                  state: {
                    groupState: effectiveGroupInfo?.state || null
                  }
                })
              }}
              title={_('Back to invitations')}
            >
              <FontAwesomeIcon icon="undo" className="mr-1" />
              {_('Back')}
            </button>
          </div>
        )}

        {!session.parentHash && origin === 'dashboard' && (
          <div>
            <button
              className="icon-button text-sm text-blue-600 hover:underline"
              onClick={() => {
                sessionStorage.removeItem('group_origin')
                navigate('/authenticated')
              }}
              title={_('Back to dashboard')}
            >
              <FontAwesomeIcon icon="undo" className="mr-1" />
              {_('Back')}
            </button>
          </div>
        )}

        <GroupHeader
          _={_}
          session={session}
          effectiveGroupInfo={effectiveGroupInfo}
          hasActiveFilters={hasActiveFilters}
          activeFilters={activeFilters}
          FILTER_LABELS={FILTER_LABELS}
          handleClearFilters={handleClearFilters}
          toggleFilters={toggleFilters}
          handleClose={handleClose}
          handleReopen={handleReopen}
          isReadOnly={isReadOnly}
          actions={
            (!isReadOnly || isClosed) ? (
              <div className="flex gap-1 mt-2 md:mt-0">
                <button
                  className="icon-button text-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  title={isClosed ? _('This group is closed.') : _('Add member')}
                  disabled={isClosed}
                  onClick={() => {
                    if (isClosed) return
                    setMemberToCreate({
                      tempId: Date.now()
                    })
                  }}
                >
                  <FontAwesomeIcon icon="plus" />
                </button>
              </div>
            ) : null
          }
        />
      </>

      {/* Sidebar de filtros (colapsable) */}
      <MembersFiltersSidebar
        open={showFilters}
        onClose={() => setShowFilters(false)}
        values={filters}
        onChange={handleFilterChange}
        onApply={handleApplyFilters}
        onClear={handleClearFilters}
      />

      {loading ? (
        <div className="text-[#60748a] text-sm">
          {_('Loading members...')}
        </div>
      ) : (
        <div>
          <MembersTable
            members={members}
            session={session}
            onEdit={handleEdit}
            onDelete={handleAskDelete}
            isReadOnly={isReadOnly}
            isLeader={session?.role === 'leader' || isLeaderActingAsGuest}
          />

          {/* Paginación simple */}
          <div className="flex items-center justify-end gap-3 mt-3">
            <span className="text-sm text-[#60748a]">
              {_('Total')}: {total} · {_('Page')}: {page}/{totalPages}
            </span>
            <button
              className="button-secondary px-2 py-1"
              disabled={!canPrev}
              onClick={() => canPrev && setPage(p => Math.max(p - 1, 1))}
              title={_('Previous')}
            >
              {_('Prev')}
            </button>
            <button
              className="button-secondary px-2 py-1"
              disabled={!canNext}
              onClick={() =>
                canNext && setPage(p => Math.min(p + 1, totalPages))
              }
              title={_('Next')}
            >
              {_('Next')}
            </button>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="modal-overlay">
          <div className="modal-box">
            <p>
              {_('Are you sure you want to exclude')}{' '}
              <strong>{deleteTarget.firstName} {deleteTarget.lastName}</strong>{' '}
              {_('from the group?')}
            </p>

            <div className="mt-4 flex justify-end gap-2">
              <button
                className="button-secondary"
                onClick={() => setDeleteTarget(null)}
              >
                {_('Cancel')}
              </button>
              <button className="button-danger" onClick={handleDelete}>
                {_('Confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {memberToEdit && (
        <MemberEditorModal
          mode="edit"
          editedMember={memberToEdit}
          onChange={handleChange}
          onSave={handleSave}
          onClose={handleCancel}
          errorMessage={editError}
          errorName={editErrorName}
          errorCode={editErrorCode}   // <— nuevo
        />
      )}

      {memberToCreate && (
        <MemberEditorModal
          mode="create"
          editedMember={memberToCreate}
          onChange={(e) => {
            const { name, value } = e.target
            if (name === '__bulk__') {
              setMemberToCreate(prev => ({ ...prev, ...value }))
            } else {
              setMemberToCreate(prev => ({ ...prev, [name]: value }))
            }
          }}
          onSave={handleCreate}
          onClose={() => {
            setMemberToCreate(null)
            setCreateError('')
            setErrorName('')
          }}
          errorMessage={createError}
          errorName={errorName}
        />
      )}
    </div>
  )
}
